import { useEffect, RefObject } from "react";

type Event = MouseEvent | TouchEvent;

/**
 * Hook that handles click/touch events outside of a specified element
 *
 * @param ref - Reference to the element to detect clicks outside of
 * @param handler - Callback function to run when click outside is detected
 */
export function useOnClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>,
  handler: (event: Event) => void,
): void {
  useEffect(() => {
    // Return early if no ref or handler provided
    if (!ref.current || !handler) {
      return;
    }

    const listener = (event: Event) => {
      // Do nothing if clicking on null or the referenced element
      const el = ref.current;

      if (!el || el.contains((event.target as Node) || null)) {
        return;
      }

      // Call the handler if click is outside
      handler(event);
    };

    // Add event listeners
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    // Clean up event listeners
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}
